/**
 * What Stripe is doing about money for an organization. Mirrors `displayStatus` on the server.
 *
 * This says NOTHING about which plan they are on — that is the Plan column's job, and a status
 * column that repeats it can only ever contradict it. An earlier version returned "Starter" when
 * there was no Stripe subscription, which showed `Plan: Enterprise / Status: Starter` on every
 * backfilled organization: a plan name wearing a status column's clothes.
 *
 * Derived, never stored narrowed. `past_due` needs its own value or an admin cannot answer the
 * only question this surface exists for: who is behind on payment. It is a real state and a
 * lasting one — Stripe retries for weeks and deliberately does not cancel, and the client keeps
 * their entitlements throughout.
 *
 * The two blanks are different facts and are kept apart:
 *   "Not billed" — we hold a plan for them, and no money moves through Stripe for it. Every
 *                  Starter organization, and any contract not yet minted a price.
 *   "—"          — no projection row at all. We do not know, and guessing would assert a plan
 *                  nobody recorded.
 */
export type SubscriptionDisplayStatus =
  | "Active"
  | "Past due"
  | "Cancelling"
  | "Cancelled"
  | "Not billed"
  | "—";

export function subscriptionDisplayStatus(row: {
  subscriptionStatus?: string | null;
  status?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  hasSubscription?: boolean | null;
  stripeSubscriptionId?: string | null;
}): SubscriptionDisplayStatus {
  const status = row.subscriptionStatus ?? row.status ?? null;
  if (status === null) return "—";

  // Outranks the stored status. A backfilled row carries `active` because the organization really
  // is on that plan, but there is no Stripe subscription behind it to be active OR past due.
  const hasSub = row.hasSubscription ?? row.stripeSubscriptionId != null;
  if (!hasSub) return "Not billed";

  if (status === "past_due" || status === "unpaid") return "Past due";
  if (status === "canceled") return "Cancelled";
  if (row.cancelAtPeriodEnd) return "Cancelling";
  return "Active";
}

/** Chip colour for a status. Past due is the one that has to be noticed. */
export function subscriptionStatusColor(
  s: SubscriptionDisplayStatus,
): "default" | "success" | "warning" | "error" {
  if (s === "Past due") return "error";
  if (s === "Cancelling") return "warning";
  if (s === "Active") return "success";
  return "default";
}
